// What the Data & sets module computes from a project, as pure functions, so the same
// code runs in the app and in the parity tests (test/parity.test.ts). The order and
// arguments follow DipField 1.0.0 exactly. GPL-3.0-or-later.
import * as S from "@dipfield/stereo";
import type { Project } from "@dipfield/project";

export interface Derived {
  poles: S.Vec3[];
  /** Terzaghi weight of each row (TRZ-01..04); 1 for spot mapping. */
  tWeights: number[];
  /** quantity × Terzaghi weight when weighting is on, else quantity. */
  weights: number[];
  /** Set of each pole, 0 = none (SET-02). */
  assignment: Int32Array;
  summaries: S.SetSummary[];
  stats: S.FisherStats | null;
  gm: S.Vec3 | null;
  bm: S.Vec3 | null;
}

/** Is any traverse a scanline or a face (so weighting means something)? */
export const anyBiased = (p: Project) => p.data.traverses.some((t) => t.tr.kind !== "spot");
/** Is Terzaghi weighting applied to the results right now? */
export const weighted = (p: Project) => p.data.terzaghi.use && anyBiased(p);

export function derive(p: Project): Derived {
  const planes = p.data.planes;
  const poles = planes.map((q) => S.planeToPole(q.dip, q.dipDir));
  const tIndex = new Map(p.data.traverses.map((t, i) => [t.id, i] as [string, number]));
  const traverseOf = planes.map((q) => tIndex.get(q.traverse ?? "") ?? 0);
  const tWeights = S.terzaghiWeightsMulti(poles, traverseOf, p.data.traverses.map((t) => t.tr), p.data.terzaghi.minBiasDeg);
  const weights = planes.map((q, i) => q.quantity * (p.data.terzaghi.use ? tWeights[i] : 1));
  const assignment = S.assignSets(poles, p.sets);
  const summaries = S.summariseSets(poles, p.sets, assignment, weights);
  const n = poles.length;
  return {
    poles, tWeights, weights, assignment, summaries,
    stats: n >= 2 ? S.fisherStats(poles, { weights }) : null,
    gm: n ? S.globalMean(poles, weights) : null,
    bm: n >= 3 ? S.globalBestFit(poles, weights) : null,
  };
}

/** Arguments of the density job: the DIPS grid (DEN-01), so values compare 1:1 with DIPS. */
export function densityArgs(p: Project, d: Derived) {
  const s = p.stereo.density;
  return { poles: d.poles, proj: p.stereo.projection,
    opts: { size: S.DIPS_GRID_SIZE, method: s.method as S.DensityMethod, weights: d.weights, countCircle: s.countCircle, coneRule: s.coneRule as S.ConeRule } };
}
export type DensityResult = { grid: S.DensityGrid; levels: number[] };
export function computeDensity(args: ReturnType<typeof densityArgs>): DensityResult {
  const grid = S.densityGrid(args.poles, args.proj, args.opts);
  return { grid, levels: S.autoLevels(grid.max, 7) };
}

/** Arguments of the DSE job (DSE-01..05). Weights only when Terzaghi weighting is on. */
export function dseArgs(p: Project, d: Derived) {
  const o = p.stereo.dse, useW = o.useWeights && weighted(p);
  return { dipdir: p.data.planes.map((q) => q.dipDir), dip: p.data.planes.map((q) => q.dip),
    o: { projection: o.projection, maxSets: o.maxSets, minAngleDeg: o.minAngleDeg, coneDeg: o.coneDeg, minPeakPct: o.minPeakPct, weights: useW ? d.weights : undefined } };
}
export type DseResult = ReturnType<typeof S.dseIdentifySets>;
export const computeDse = (a: ReturnType<typeof dseArgs>): DseResult => S.dseIdentifySets(a.dipdir, a.dip, a.o);

/** Contour lines as SVG path data on a net of radius `R` centred at (c, c): the
 *  exported stereonet's contour layer (DEN-06, DEN-08). */
export function contourPath(grid: S.DensityGrid, levels: number[], c = 360, R = 300): string {
  const T = (q: S.XY) => `${(c + q.x * R).toFixed(2)},${(c - q.y * R).toFixed(2)}`;
  return levels.map((lv) => S.contourSegments(grid, lv).map(([a, b]) => `M${T(a)}L${T(b)}`).join("")).join("");
}
